﻿<template>
  <div>
    <b-card>
      <div class="team-toolbar">
        <div class="team-toolbar-left">
          <h4 class="mb-0">Team Members</h4>
          <small class="text-muted d-none d-lg-inline">Manage staff access, roles, and status</small>
        </div>
        <div class="team-toolbar-right">
          <div class="team-search d-flex align-items-center">
            <b-form-input
              v-model.trim="filters.search"
              class="mr-1"
              size="sm"
              placeholder="Search by name, email or mobile"
              @input="queueSearch"
              @keyup.enter="applySearch"
            />
            <b-button
              variant="outline-primary"
              size="sm"
              @click="applySearch"
            >
              Search
            </b-button>
          </div>
          <div class="team-filters d-flex align-items-center">
            <b-button-group size="sm" class="mr-1 mb-1 mb-md-0">
              <b-button
                v-for="option in statusFilters"
                :key="option.value"
                :variant="filters.status === option.value ? 'primary' : 'outline-primary'"
                @click="setStatusFilter(option.value)"
              >
                {{ option.label }}
              </b-button>
            </b-button-group>
            <v-select
              v-model="filters.role"
              class="team-role-select mr-1 mb-1 mb-md-0"
              :options="roleOptions"
              :reduce="role => role.id"
              label="name"
              :clearable="true"
              placeholder="Filter by role"
            />
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mb-1 mb-md-0"
              @click="clearFilters"
            >
              Clear
            </b-button>
          </div>
          <b-button
            v-if="canManageTeam"
            variant="primary"
            size="sm"
            class="team-add-button"
            @click="openCreateModal"
          >
            Add Team Member
          </b-button>
        </div>
      </div>

      <div class="d-none d-md-block">
        <b-table
          :items="filteredStaff"
          :fields="tableFieldsComputed"
          striped
          hover
          responsive
          :busy="loading"
          show-empty
          empty-text="No staff member found"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
        >
          <template #table-busy>
            <div class="table-skeleton p-1">
              <div v-for="n in 5" :key="`sk-row-${n}`" class="skeleton-row">
                <span class="skeleton-bar sk-w-20" />
                <span class="skeleton-bar sk-w-25" />
                <span class="skeleton-bar sk-w-20" />
                <span class="skeleton-bar sk-w-15" />
                <span class="skeleton-bar sk-w-10" />
              </div>
            </div>
          </template>
          <template #cell(roles)="data">
            <div>
              <b-badge
                v-for="role in data.item.roles"
                :key="role.id"
                variant="light-primary"
                class="mr-50 mb-50 text-capitalize"
              >
                {{ role.name }}
              </b-badge>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              :variant="data.item.status ? 'light-success' : 'light-secondary'"
            >
              {{ data.item.status ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>

          <template
            v-if="canManageTeam"
            #cell(actions)="data"
          >
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none p-0"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openEditModal(data.item)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                Edit
              </b-dropdown-item>
              <b-dropdown-item @click="reinviteStaff(data.item)">
                <feather-icon icon="SendIcon" class="mr-50" />
                Re-invite
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(data.item)">
                <feather-icon icon="Trash2Icon" class="mr-50" />
                Remove
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </div>

      <div class="d-md-none">
        <div v-if="loading" class="team-card-skeleton">
          <div v-for="n in 3" :key="`sk-card-${n}`" class="team-card skeleton-card mb-1">
            <span class="skeleton-bar sk-w-40 mb-50" />
            <span class="skeleton-bar sk-w-60 mb-50" />
            <span class="skeleton-bar sk-w-30 mb-50" />
            <span class="skeleton-bar sk-w-50" />
          </div>
        </div>
        <div
          v-else-if="filteredStaff.length === 0"
          class="text-center text-muted py-2"
        >
          No staff member found
        </div>
        <b-card
          v-else
          v-for="item in filteredStaff"
          :key="item.id"
          class="team-card mb-1"
        >
          <div class="d-flex align-items-start justify-content-between">
            <div>
              <h5 class="mb-25">{{ item.name }}</h5>
              <small class="text-muted d-block">{{ item.email || 'No email' }}</small>
            </div>
            <b-badge :variant="item.status ? 'light-success' : 'light-secondary'">
              {{ item.status ? 'Active' : 'Inactive' }}
            </b-badge>
          </div>
          <div class="mt-50">
            <small class="text-muted text-uppercase d-block mb-25">Mobile</small>
            <div class="font-weight-bolder">{{ item.mobile || '—' }}</div>
          </div>
          <div class="mt-50">
            <small class="text-muted text-uppercase d-block mb-25">Roles</small>
            <div class="d-flex flex-wrap">
              <b-badge
                v-for="role in item.roles"
                :key="role.id"
                variant="light-primary"
                class="mr-50 mb-50 text-capitalize"
              >
                {{ role.name }}
              </b-badge>
            </div>
          </div>
          <div
            v-if="canManageTeam"
            class="d-flex align-items-center justify-content-between mt-1"
          >
            <small class="text-muted">Actions</small>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none p-0"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item @click="openEditModal(item)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                Edit
              </b-dropdown-item>
              <b-dropdown-item @click="reinviteStaff(item)">
                <feather-icon icon="SendIcon" class="mr-50" />
                Re-invite
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDelete(item)">
                <feather-icon icon="Trash2Icon" class="mr-50" />
                Remove
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-card>
      </div>

      <div class="d-flex flex-wrap justify-content-between align-items-center mt-1">
        <small class="text-muted">
          Showing {{ filteredStaff.length }} of {{ meta.total }} members
        </small>
        <b-pagination
          v-if="meta.last_page > 1"
          v-model="filters.page"
          :total-rows="meta.total"
          :per-page="meta.per_page"
          size="sm"
          @input="fetchStaff"
        />
      </div>
    </b-card>

    <b-card
      v-if="inviteInfo"
      class="mt-1"
      border-variant="warning"
    >
      <h6 class="text-warning mb-1">
        Save these temporary credentials securely
      </h6>
      <p class="mb-25">
        <strong>Member:</strong> {{ (inviteInfo.staff && inviteInfo.staff.name) || '' }}
      </p>
      <p class="mb-25">
        <strong>Temporary Password:</strong> {{ inviteInfo.temporary_password }}
      </p>
      <p class="mb-0">
        <strong>Invitation Token:</strong> {{ inviteInfo.invitation_token }}
      </p>
    </b-card>

    <b-modal
      id="staff-form-modal"
      v-model="showFormModal"
      modal-class="team-member-modal"
      dialog-class="team-member-modal__dialog"
      hide-header
      hide-footer
      no-close-on-backdrop
      scrollable
      @hidden="resetForm"
    >
      <div class="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
        <h5 class="mb-0">{{ isEdit ? 'Update Team Member' : 'Add Team Member' }}</h5>
        <b-button
          size="sm"
          variant="link"
          class="text-body"
          @click="closeModal"
        >
          Close
        </b-button>
      </div>
      <validation-observer ref="staffForm">
        <b-form class="p-2" @submit.prevent="submitForm">
          <b-row>
            <b-col md="6">
              <b-form-group label="Full Name" label-for="staff-name">
                <validation-provider
                  name="Name"
                  v-validate="required"
                  #default="{ errors }"
                >
                  <b-form-input
                    id="staff-name"
                    v-model="form.name"
                    :state="errors.length ? false : null"
                    placeholder="Enter full name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Email" label-for="staff-email">
                <b-form-input
                  id="staff-email"
                  v-model="form.email"
                  type="email"
                  name="email"
                  autocomplete="section-staff email"
                  inputmode="email"
                  autocapitalize="none"
                  spellcheck="false"
                  placeholder="name@example.com"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Mobile" label-for="staff-mobile">
                <validation-provider
                  name="Mobile"
                  :rules="{ required: true, validPhone: form.countryCode }"
                  #default="{ errors }"
                >
                  <div>
                    <input type="hidden" v-model="form.mobile">
                    <div class="phone-input-group">
                      <v-select
                        class="phone-dial-select"
                        :options="dialCodeOptions"
                        :value="form.phone_country"
                        :clearable="false"
                        label="name"
                        @input="handleDialCodeChange"
                      >
                        <template #option="{ flag, name, dial_code }">
                          <span class="mr-50">{{ flag }}</span>
                          <span>{{ name }}</span>
                          <span class="text-muted ml-auto">{{ dial_code }}</span>
                        </template>
                        <template #selected-option="{ flag, dial_code }">
                          <span class="mr-50">{{ flag }}</span>
                          <span>{{ dial_code }}</span>
                        </template>
                      </v-select>
                      <b-form-input
                        id="staff-mobile"
                        type="tel"
                        :value="form.phone_number"
                        name="tel"
                        autocomplete="section-staff tel"
                        inputmode="tel"
                        class="phone-number-input"
                        placeholder="Phone number"
                        @input="handlePhoneInput"
                      />
                    </div>
                    <small class="text-muted d-block mt-25">
                      E.164: {{ form.mobile || '—' }}
                    </small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </div>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Password" label-for="staff-password">
                <validation-provider
                  vid="staffPassword"
                  name="Password"
                  :rules="passwordRules"
                  #default="{ errors }"
                >
                  <b-form-input
                    id="staff-password"
                    v-model="form.password"
                    type="password"
                    :state="errors.length ? false : null"
                    placeholder="Set a password"
                  />
                  <small
                    v-if="isEdit"
                    class="text-muted d-block mt-25"
                  >
                    Leave blank to keep current password
                  </small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Confirm Password" label-for="staff-password-confirm">
                <validation-provider
                  name="Confirm Password"
                  :rules="confirmPasswordRules"
                  #default="{ errors }"
                >
                  <b-form-input
                    id="staff-password-confirm"
                    v-model="form.password_confirmation"
                    type="password"
                    :state="errors.length ? false : null"
                    placeholder="Re-type password"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Roles" label-for="staff-roles">
                <validation-provider
                  name="Roles"
                  v-validate="required"
                  #default="{ errors }"
                >
                  <v-select
                    id="staff-roles"
                    v-model="form.roles"
                    :options="roleOptions"
                    :reduce="role => role.id"
                    label="name"
                    multiple
                    placeholder="Select roles"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Extra Permissions" label-for="staff-permissions">
                <v-select
                  id="staff-permissions"
                  v-model="form.permissions"
                  :options="permissionOptions"
                  multiple
                  placeholder="Select additional permissions"
                  label="name"
                  :reduce="permission => permission.name"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Designation" label-for="staff-designation">
                <b-form-input
                  id="staff-designation"
                  v-model="form.staff_meta.designation"
                  placeholder="e.g. Sr. Support Engineer"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Department" label-for="staff-department">
                <b-form-input
                  id="staff-department"
                  v-model="form.staff_meta.department"
                  placeholder="Support / Billing / Sales"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Notes" label-for="staff-notes">
                <b-form-textarea
                  id="staff-notes"
                  v-model="form.staff_meta.notes"
                  rows="2"
                  placeholder="Optional notes to help identify this team member"
                  no-resize
                />
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex flex-wrap align-items-center justify-content-between pt-1 border-top">
            <div class="d-flex align-items-center mb-1 mb-md-0">
              <span class="text-muted mr-50">Status</span>
              <b-form-checkbox
                v-model="form.status"
                :value="1"
                :unchecked-value="0"
                switch
                class="mb-0"
              >
                {{ form.status ? 'Active' : 'Inactive' }}
              </b-form-checkbox>
            </div>
            <div class="d-flex">
              <b-button
                variant="outline-secondary"
                class="mr-1"
                @click="closeModal"
              >
                Cancel
              </b-button>
              <b-button
                variant="primary"
                :disabled="saving"
                @click="submitForm"
              >
                {{ saving ? 'Saving...' : (isEdit ? 'Update' : 'Create') }}
              </b-button>
            </div>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-card class="mt-2 role-guide-card">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-1">
        <div>
          <h5 class="mb-25">রোল ও পারমিশন গাইড</h5>
          <p class="text-muted mb-0">
            Team Members ফর্মের Roles ড্রপডাউনে বর্ণনা দেখতে হোভার করুন। নিচে বিস্তারিত বাংলা ডকুমেন্টেশন রয়েছে।
          </p>
        </div>
        <b-badge variant="light-primary" class="text-uppercase">
          ISP Admin Only
        </b-badge>
      </div>

      <b-alert variant="light-primary" show class="mb-1">
        রোল/পারমিশন ম্যানেজমেন্ট শুধুমাত্র ISP Admin বা মালিক পর্যায়ের ব্যবহারকারীরা করবেন।
      </b-alert>

      <b-row>

        <b-col
          v-for="guide in roleGuides"
          :key="guide.name"
          md="6"
          class="mb-1"
        >
          <div class="role-guide-box h-100">
            <div class="d-flex justify-content-between align-items-start mb-50">
              <h6 class="mb-0">{{ guide.name }}</h6>
              <b-badge
                v-if="guide.recommendedFor"
                variant="light-secondary"
                pill
              >
                {{ guide.recommendedFor }}
              </b-badge>
            </div>
            <p class="text-muted small mb-50">
              {{ guide.summary }}
            </p>
            <ul class="role-duties mb-0">
              <li v-for="task in guide.tasks" :key="`${guide.name}-${task}`">
                {{ task }}
              </li>
            </ul>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BModal,
  BFormCheckbox,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue'
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate'
import VSelect from 'vue-select'
import useJwt from '@/auth/jwt/useJwt'
import Mixin from '@core/services/mixin'
import dialCodeOptions from '@/data/phoneDialCodes'
import staffRoleInfo from '@/data/staffRoleInfo'
import { required } from '@validations'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

extend('validPhone', {
  params: ['country'],
  validate(value, { country }) {
    const rawValue = typeof value === 'string' ? value.trim() : ''
    if (!rawValue) return false
    const phone = parsePhoneNumberFromString(rawValue, country || undefined)
    return phone ? phone.isValid() : false
  },
  message: 'Enter a valid mobile number',
})

export default {
  name: 'IspTeamMembers',
  components: {
    BAlert,
    BBadge,
    BButton,
    BButtonGroup,
    BCard,
    BCol,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormCheckbox,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BPagination,
    BRow,
    BTable,
    ValidationObserver,
    ValidationProvider,
    VSelect,
  },
  mixins: [Mixin],
  data() {
    return {
      loading: false,
      saving: false,
      staff: [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
      },
      filters: {
        search: '',
        page: 1,
        status: 'all',
        role: null,
      },
      searchTimer: null,
      showFormModal: false,
      isEdit: false,
      form: this.defaultForm(),
      roleOptions: [],
      permissionOptions: [],
      inviteInfo: null,
      required,
      dialCodeOptions,
      roleGuides: staffRoleInfo,
      sortBy: null,
      sortDesc: false,
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'mobile', label: 'Mobile', sortable: true },
        { key: 'roles', label: 'Roles' },
        { key: 'status', label: 'Status', sortable: true, class: 'text-center' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
      statusFilters: [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 0 },
      ],
    }
  },
  created() {
    this.fetchLookups()
    this.fetchStaff()
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
  },
  computed: {
    canManageTeam() {
      return this.$ability.can('manage', 'staff') || this.$ability.can('manage', 'all')
    },
    tableFieldsComputed() {
      if (this.canManageTeam) return this.tableFields
      return this.tableFields.filter(field => field.key !== 'actions')
    },
    passwordRules() {
      if (this.isEdit) {
        return this.form.password ? 'min:8' : ''
      }
      return 'required|min:8'
    },
    confirmPasswordRules() {
      if (this.isEdit && !this.form.password) {
        return ''
      }
      return 'required|confirmed:staffPassword'
    },
    filteredStaff() {
      let items = Array.isArray(this.staff) ? [...this.staff] : []
      if (this.filters.status !== 'all') {
        const statusValue = Number(this.filters.status)
        items = items.filter(item => Number(item.status) === statusValue)
      }
      if (this.filters.role) {
        items = items.filter(item => (item.roles || []).some(role => role.id === this.filters.role))
      }
      return items
    },
  },
  methods: {
    defaultForm() {
      return {
        id: null,
        name: '',
        email: '',
        mobile: '',
        countryCode: '',
        countryCallingCode: '',
        phone_country: this.getDefaultDialOption(),
        phone_number: '',
        status: 1,
        password: '',
        password_confirmation: '',
        roles: [],
        permissions: [],
        staff_meta: {
          designation: '',
          department: '',
          notes: '',
        },
      }
    },
    async fetchLookups() {
      try {
        const [rolesRes, permsRes] = await Promise.all([
          useJwt.fetchIspStaffRoles(),
          useJwt.fetchIspStaffPermissions(),
        ])
        const fetchedRoles = rolesRes.data.data.roles || rolesRes.data.roles || []
        this.roleOptions = fetchedRoles.map(role => this.decorateRoleOption(role))
        this.permissionOptions = (permsRes.data.data.permissions || permsRes.data.permissions || []).map(permission => ({
          ...permission,
          label: permission.name,
        }))
      } catch (error) {
        this.toastMessage('danger', 'Team', error)
      }
    },
    async fetchStaff(page = this.filters.page) {
      this.loading = true
      try {
        const response = await useJwt.fetchIspStaff({
          page,
          search: this.filters.search || undefined,
        })
        const payload = response.data.data || {}
        const collection = payload.staff || {}
        const collectionData = collection.data || []
        const collectionMeta = collection.meta || {}

        this.staff = collectionData
        this.meta = {
          current_page: collectionMeta.current_page || 1,
          last_page: collectionMeta.last_page || 1,
          per_page: collectionMeta.per_page || collectionData.length || 20,
          total: collectionMeta.total || collectionData.length || 0,
        }
        this.filters.page = this.meta.current_page
      } catch (error) {
        this.toastMessage('danger', 'Team', error)
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      if (!this.canManageTeam) return
      this.inviteInfo = null
      this.isEdit = false
      this.form = this.defaultForm()
      this.showFormModal = true
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.applySearch()
      }, 400)
    },
    applySearch() {
      this.filters.page = 1
      this.fetchStaff(1)
    },
    setStatusFilter(value) {
      this.filters.status = value
    },
    clearFilters() {
      this.filters.search = ''
      this.filters.status = 'all'
      this.filters.role = null
      this.applySearch()
    },
    openEditModal(item) {
      if (!this.canManageTeam) return
      this.inviteInfo = null
      this.isEdit = true
      this.form = {
        id: item.id,
        name: item.name,
        email: item.email,
        mobile: item.mobile,
        countryCode: item.countryCode || '',
        countryCallingCode: item.countryCallingCode || '',
        phone_country: this.getDefaultDialOption(),
        phone_number: '',
        status: item.status,
        password: '',
        password_confirmation: '',
        roles: (item.roles || []).map(role => role.id),
        permissions: (item.permissions || []).slice(),
        staff_meta: {
          designation: item.staff_meta?.designation || '',
          department: item.staff_meta?.department || '',
          notes: item.staff_meta?.notes || '',
        },
      }
      this.hydratePhoneFields(item.mobile)
      this.showFormModal = true
    },
    closeModal() {
      this.showFormModal = false
    },
    resetForm() {
      this.saving = false
      this.form = this.defaultForm()
      if (this.$refs.staffForm) {
        this.$refs.staffForm.reset()
      }
    },
    decorateRoleOption(role) {
      const guide = this.roleGuides.find(item => item.name === role.name) || {}
      return {
        ...role,
        summary: guide.summary,
        tasks: guide.tasks,
        recommendedFor: guide.recommendedFor,
      }
    },
    getDefaultDialOption() {
      return dialCodeOptions.find(option => option.iso2 === 'BD') || dialCodeOptions[0] || null
    },
    sanitizeDigits(value) {
      return (value || '').replace(/\D/g, '')
    },
    handleDialCodeChange(option) {
      this.form.phone_country = option || this.getDefaultDialOption()
      this.updateE164()
    },
    handlePhoneInput(value) {
      const digits = this.sanitizeDigits(value)
      this.form.phone_number = this.normalizeNationalNumber(digits)
      this.updateE164()
    },
    normalizeNationalNumber(digits) {
      let normalized = this.sanitizeDigits(digits).replace(/^0+/, '')
      const country = this.form.phone_country
      const callingCode = country ? this.sanitizeDigits(country.dial_code) : ''
      if (callingCode) {
        while (normalized.startsWith(callingCode) && normalized.length > callingCode.length) {
          normalized = normalized.slice(callingCode.length)
        }
      }
      return normalized
    },
    updateE164() {
      const country = this.form.phone_country
      const digits = this.normalizeNationalNumber(this.form.phone_number)
      this.form.countryCode = country ? country.iso2 : ''
      this.form.countryCallingCode = country ? this.sanitizeDigits(country.dial_code) : ''
      if (country && digits) {
        this.form.mobile = `${country.dial_code}${digits}`
      } else {
        this.form.mobile = ''
      }
    },
    matchDialCode(mobile) {
      if (!mobile) return this.getDefaultDialOption()
      const normalized = mobile.trim()
      const sorted = [...dialCodeOptions].sort((a, b) => b.dial_code.length - a.dial_code.length)
      return sorted.find(option => normalized.startsWith(option.dial_code)) || this.getDefaultDialOption()
    },
    hydratePhoneFields(mobile) {
      const normalized = typeof mobile === 'string' ? mobile.trim() : ''
      const dialOption = this.matchDialCode(normalized)
      this.form.phone_country = dialOption
      const numberPart = dialOption && normalized.startsWith(dialOption.dial_code)
        ? normalized.slice(dialOption.dial_code.length)
        : normalized.replace(/^\+/, '')
      this.form.phone_number = this.sanitizeDigits(numberPart)
      this.updateE164()
    },
    async submitForm() {
      if (!this.canManageTeam) {
        this.closeModal()
        return
      }
      const isValid = await this.$refs.staffForm.validate()
      if (!isValid) return
      this.saving = true
      const payload = {
        name: this.form.name,
        email: this.form.email || null,
        mobile: this.form.mobile || null,
        countryCode: this.form.countryCode || null,
        countryCallingCode: this.form.countryCallingCode || null,
        status: this.form.status,
        roles: this.form.roles,
        permissions: this.form.permissions,
        staff_meta: this.form.staff_meta,
      }
      if (this.form.password) {
        payload.password = this.form.password
        payload.password_confirmation = this.form.password_confirmation
      }
      try {
        if (this.isEdit && this.form.id) {
          await useJwt.updateIspStaff(this.form.id, payload)
          this.toastMessage('success', 'Team', { data: { messages: ['Team member updated'] } })
        } else {
          const response = await useJwt.createIspStaff(payload)
          const invitePayload = response.data.data || {}
          this.inviteInfo = invitePayload.temporary_password ? invitePayload : null
          this.toastMessage('success', 'Team', response)
        }
        this.fetchStaff()
        this.closeModal()
      } catch (error) {
        this.toastMessage('danger', 'Team', error)
      } finally {
        this.saving = false
      }
    },
    async confirmDelete(staff) {
      if (!this.canManageTeam) return
      const confirm = await this.$bvModal.msgBoxConfirm(`Remove ${staff.name}?`, {
        title: 'Remove Team Member',
        okVariant: 'danger',
        okTitle: 'Yes, Remove',
        cancelTitle: 'Cancel',
      })
      if (!confirm) return
      try {
        await useJwt.deleteIspStaff(staff.id)
        this.toastMessage('success', 'Team', { data: { messages: ['Team member removed'] } })
        this.fetchStaff()
      } catch (error) {
        this.toastMessage('danger', 'Team', error)
      }
    },
    async reinviteStaff(staff) {
      if (!this.canManageTeam) return
      try {
        const response = await useJwt.reinviteIspStaff(staff.id)
        const payload = response.data.data || {}
        this.inviteInfo = payload
        this.toastMessage('success', 'Team', response)
        this.fetchStaff()
      } catch (error) {
        this.toastMessage('danger', 'Team', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}
.mb-50 {
  margin-bottom: 0.5rem;
}
.phone-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.team-toolbar {
  position: sticky;
  top: 0.25rem;
  z-index: 2;
  background: #fff;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f0f5;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.team-toolbar-left {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}
.team-toolbar-left h4 {
  font-weight: 600;
}
.team-toolbar-left small {
  line-height: 1.2;
  white-space: nowrap;
}
.team-toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.team-search {
  gap: 0.5rem;
  flex-wrap: nowrap;
}
.team-search .form-control {
  min-width: 180px;
  max-width: 220px;
}
.team-filters {
  gap: 0.5rem;
  flex-wrap: nowrap;
}
.team-add-button {
  white-space: nowrap;
}
.team-role-select {
  min-width: 180px;
}
.team-card {
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
}
.team-card h5 {
  font-weight: 600;
}
.team-member-modal__dialog {
  max-width: 100vw;
  width: 100vw;
  height: 100vh;
  margin: 0;
}
.team-member-modal .modal-content {
  height: 100%;
  border-radius: 0;
}
.team-member-modal .modal-body {
  padding: 0;
  overflow: auto;
}
.table-skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.skeleton-row {
  display: flex;
  gap: 0.75rem;
}
.skeleton-bar {
  height: 12px;
  border-radius: 999px;
  background: #f1f0f5;
  position: relative;
  overflow: hidden;
}
.skeleton-bar::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent);
  animation: skeleton-shimmer 1.4s infinite;
}
.sk-w-10 { width: 10%; }
.sk-w-15 { width: 15%; }
.sk-w-20 { width: 20%; }
.sk-w-25 { width: 25%; }
.sk-w-30 { width: 30%; }
.sk-w-40 { width: 40%; }
.sk-w-50 { width: 50%; }
.sk-w-60 { width: 60%; }
.skeleton-card {
  padding: 1rem;
}
.team-card-skeleton .skeleton-bar {
  height: 10px;
}
@keyframes skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@media (min-width: 768px) {
  .phone-input-group {
    flex-direction: row;
    align-items: stretch;
  }
}

.phone-dial-select {
  flex: 0 0 120px;
  min-width: 120px;
  max-width: 140px;
}
.phone-number-input {
  flex: 1 1 0%;
  min-width: 160px;
}
.role-guide-card {
  border: 1px solid #ebe9f1;
}
.role-guide-box {
  border: 1px solid #f0f0f5;
  border-radius: 0.75rem;
  padding: 1rem;
  background: #fff;
}
.role-duties {
  list-style: disc;
  margin-left: 1.25rem;
  color: #5e5873;
  font-size: 0.9rem;
}

@media (max-width: 767.98px) {
  .team-toolbar {
    position: static;
    top: auto;
  }
  .team-toolbar-left {
    width: 100%;
    justify-content: space-between;
  }
  .team-toolbar-right,
  .team-search,
  .team-filters {
    width: 100%;
    flex-wrap: wrap;
  }
  .team-search .form-control,
  .team-role-select {
    width: 100%;
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
  .team-add-button {
    width: 100%;
  }
}

@media (min-width: 1200px) {
  .team-toolbar {
    flex-wrap: nowrap;
  }
  .team-toolbar-right {
    flex-wrap: nowrap;
  }
}
</style>

